import Modal from './Modal.jsx';
import { Button, ErrorState, Skeleton } from './ui.jsx';
import { api } from '../lib/api.js';
import { currency, rate, tenure } from '../lib/format.js';
import { useAsync } from '../lib/useAsync.js';

/**
 * ScheduleModal — the month-by-month repayment schedule for the selected plan.
 *
 * Fetched from the API rather than computed in the browser, for the same reason
 * the calculator is: one EMI engine, server-side. It also doubles as evidence
 * for the shopper that the instalments shown genuinely clear the balance — the
 * closing balance on the final row is zero.
 */
export default function ScheduleModal({ open, onClose, slug, variantId, plan }) {
  const shouldLoad = open && Boolean(slug && variantId && plan);

  const { data, error, loading, reload } = useAsync(
    (signal) =>
      shouldLoad
        ? api.getEmiSchedule(slug, { variantId, tenureMonths: plan.tenureMonths }, signal)
        : Promise.resolve(null),
    [shouldLoad, slug, variantId, plan?.tenureMonths],
  );

  const schedule = data?.schedule ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Repayment schedule"
      description={
        plan
          ? `${currency(plan.monthlyAmount)} per month for ${tenure(plan.tenureMonths)} at ${rate(plan.interestRate)} p.a.`
          : undefined
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading && (
        <div className="space-y-2" role="status" aria-label="Loading schedule">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}

      {!loading && error && <ErrorState title="Could not load the schedule" message={error.message} onRetry={reload} />}

      {!loading && !error && schedule.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Amortisation schedule showing how each instalment splits between interest and principal
              </caption>
              <thead className="text-left text-xs uppercase tracking-wide text-ink-500">
                <tr className="border-b border-ink-200">
                  <th scope="col" className="py-2.5 pr-3 font-semibold">Month</th>
                  <th scope="col" className="px-3 py-2.5 text-right font-semibold">Opening</th>
                  <th scope="col" className="px-3 py-2.5 text-right font-semibold">Instalment</th>
                  <th scope="col" className="px-3 py-2.5 text-right font-semibold">Interest</th>
                  <th scope="col" className="px-3 py-2.5 text-right font-semibold">Principal</th>
                  <th scope="col" className="py-2.5 pl-3 text-right font-semibold">Closing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {schedule.map((row) => (
                  <tr key={row.month} className="hover:bg-ink-50">
                    <th scope="row" className="py-2.5 pr-3 text-left font-semibold text-ink-900">
                      {row.month}
                    </th>
                    <td className="px-3 py-2.5 text-right text-ink-600">{currency(row.openingBalance)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-ink-900">{currency(row.instalment)}</td>
                    <td className="px-3 py-2.5 text-right text-ink-600">
                      {row.interest === 0 ? '—' : currency(row.interest)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-ink-600">{currency(row.principal)}</td>
                    <td className="py-2.5 pl-3 text-right text-ink-600">{currency(row.closingBalance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-ink-300 font-semibold">
                <tr>
                  <th scope="row" className="py-3 pr-3 text-left">
                    Total
                  </th>
                  <td />
                  <td className="px-3 py-3 text-right text-ink-950">{currency(data.plan.totalPayable)}</td>
                  <td className="px-3 py-3 text-right text-ink-950">
                    {data.plan.totalInterest === 0 ? '—' : currency(data.plan.totalInterest)}
                  </td>
                  <td className="px-3 py-3 text-right text-ink-950">{currency(data.plan.principal)}</td>
                  <td className="py-3 pl-3 text-right text-growth-700">{currency(0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-ink-500">
            Interest is charged on the reducing balance, so the interest share of each instalment falls over time
            while the principal share rises. Any rounding difference is absorbed by the final instalment, which is
            why the closing balance settles at exactly zero.
          </p>
        </>
      )}
    </Modal>
  );
}
